import { useState, lazy, Suspense } from 'react';
import { Avatar, Box, Grid, Typography } from '@mui/material';
import { Note, NoteType } from '@models/note';
import { ThumbtackSolid } from '@assets/svgs';
import { usePinNote } from '@services/note';
import { HtmlContent } from '@components/noteMessage/CardNote';
import { MenuItemType } from '@components/menu/Menu';
import { MenuWrapper } from '@components/menu/MenuWrapper';
import { CardAttachmentFile } from '@components/noteMessage/CardAttachmentFile';
import { getMediaType } from '@utils/fileUtils';
import { useDeleteNote } from '@services/note/useDeleteNote';
import { containerEditor } from '@components/richTextEditor/RichTextEditor.styles';
import { useQuote } from '@services/quotes';
import { format } from 'date-fns-tz';
import { fakeLocalMidnightFromTZMidnight, ShortDateFormat } from '@utils/date';
import { QuoteTypeLabels } from '@models/quotes';
import { useGetPolicy } from '@services/policy';
import { getOriginLine } from '@utils/recordOrigin';

const LazyRichTextEditor = lazy(() =>
  import('@components/richTextEditor/RichTextEditor').then((m) => ({ default: m.RichTextEditor })),
);

export interface DialogNoteItemProps {
  note: Note;
  searchBarText?: string;
  showMetadata?: boolean;
}

// Renders a single note inside the Notes Dialog with styling similar to main app note cards but simplified.
export const DialogNoteItem = ({ note, searchBarText, showMetadata }: DialogNoteItemProps) => {
  const { pinNote } = usePinNote();
  const { deleteNote } = useDeleteNote();
  const createdAt = new Date(note.createdAtOrReplacedAt ?? note.createdAt);
  const author = `${note.createdByUser?.firstName ?? ''} ${note.createdByUser?.lastName ?? ''}`.trim();
  const originLine = getOriginLine(note.origin);
  const [editingNote, setEditingNote] = useState(false);

  // if metadataConfig is set fetch the quote and/or policy details to show transaction type or policy number
  const { quote } = useQuote(showMetadata ? note?.quoteId : undefined);
  const { policy } = useGetPolicy({ policyId: showMetadata ? note?.policyId : undefined });

  const metaData = () => {
    if (quote) {
      const formattedEffectiveDate = quote?.effectiveOn
        ? `${format(fakeLocalMidnightFromTZMidnight(new Date(quote.effectiveOn)), ShortDateFormat)}`
        : null;

      return (
        <Typography component='span' variant='caption' sx={sxNoteItem.metaPill}>
          {QuoteTypeLabels[quote.quoteType]} • Effective On: {formattedEffectiveDate}
        </Typography>
      );
    }

    if (policy) {
      const formattedEffectiveDate = policy?.effectiveOn
        ? `${format(fakeLocalMidnightFromTZMidnight(new Date(policy.effectiveOn)), ShortDateFormat)}`
        : null;

      return (
        <Typography component='span' variant='caption' sx={sxNoteItem.metaPill}>
          Policy • Effective On: {formattedEffectiveDate}
        </Typography>
      );
    }
    return null;
  };

  const handleOnDelete = async () => {
    await deleteNote({
      noteId: note.id,
    });
  };

  const handleEditNote = () => {
    setEditingNote(true);
  };

  return (
    <Box sx={sxNoteItem.root}>
      {note.isPinned && (
        <Box aria-label='Pinned note' sx={sxNoteItem.pinnedBadge}>
          <ThumbtackSolid />
        </Box>
      )}
      {/*  Metadata */}
      <Box sx={sxNoteItem.metaRow}>
        <Box sx={sxNoteItem.metaCol}>
          <Typography variant='caption' sx={sxNoteItem.metaTimestamp}>
            <span>{createdAt.toLocaleDateString()}</span>
            <span>{createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            {showMetadata && <>{metaData()}</>}
          </Typography>
        </Box>
        {!editingNote && (
          <MenuWrapper
            items={[
              {
                type: MenuItemType.Default,
                text: note.isPinned ? 'Unpin Note' : 'Pin Note',
                handleOnClick: () => {
                  pinNote({ noteId: note.id });
                },
              },
              {
                type: MenuItemType.Default,
                text: 'Edit Note',
                handleOnClick: handleEditNote,
              },
              {
                type: MenuItemType.Default,
                text: 'Delete Note',
                handleOnClick: handleOnDelete,
              },
            ]}
          />
        )}
      </Box>
      {/* Message */}
      <Box sx={sxNoteItem.message}>
        {editingNote ? (
          <Box
            sx={containerEditor}
            style={{
              padding: '0px',
            }}
          >
            <Suspense fallback={null}>
              <LazyRichTextEditor
                type={NoteType.Note}
                editConfig={{
                  body: note.body,
                  editing: true,
                  editingAttachments: note.artifacts,
                  onCancelEdit: () => setEditingNote(false),
                  isPinned: note.isPinned,
                  noteId: note.id,
                  quoteId: note.quoteId,
                  customerId: note.customerId,
                  brokerageId: note.brokerageId,
                  policyId: note.policyId,
                }}
              />
            </Suspense>
          </Box>
        ) : (
          <HtmlContent
            body={note.body}
            searchBarText={searchBarText}
            sxProps={{
              color: 'inherit',
              lineHeight: '20px',
              '& p': { margin: 0 },
              '& p + p': { marginTop: '4px' },
            }}
          />
        )}
      </Box>
      {/* Footer */}
      <Box sx={sxNoteItem.footerRow}>
        <Box sx={sxNoteItem.footerCol}>
          {note?.artifacts && note.artifacts.length > 0 && (
            <Grid spacing={0.5} sx={sxNoteItem.attachments.wrapper}>
              <Typography variant='caption' color='text.subdued'>
                Attachments
              </Typography>
              {note.artifacts.map((artifact: any) => (
                <Grid key={artifact.id} sx={sxNoteItem.attachments.item}>
                  <CardAttachmentFile
                    {...artifact}
                    description={null}
                    variant='dense'
                    allowViewInModal
                    type={getMediaType(artifact.type)}
                    isNote={false}
                    state='sent'
                    menuItems={[]}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          <Typography variant='caption' sx={sxNoteItem.author}>
            <Avatar sx={sxNoteItem.authorAvatar} />
            <span>{author ?? 'Unknown'}</span>
          </Typography>
          {originLine && (
            <Typography variant='caption' color='text.secondary'>
              {originLine.label}: {originLine.value}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Consolidated style object to keep JSX clean
const sxNoteItem = {
  root: {
    p: 1.25,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    borderRadius: 4,
    backgroundColor: 'background.paper',
    boxShadow: 2,
    position: 'relative',
    overflow: 'visible',
  },
  pinnedBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    background: 'white',
    borderRadius: '50%',
    p: '2px',
    color: 'secondary.main',
    boxShadow: 2,
    lineHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    svg: { width: 14, height: 14 },
  },
  metaRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 },
  metaCol: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  metaTimestamp: {
    alignItems: 'center',
    fontWeight: 500,
    color: 'text.secondary',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
  },
  metaPill: {
    display: 'inline-block',
    lineHeight: 1,
    px: 0.75,
    py: 0.25,
    width: 'fit-content',
    backgroundColor: 'grey.100',
    border: '1px solid',
    borderColor: 'grey.300',
    color: 'text.secondary',
    borderRadius: 50,
    letterSpacing: 0.1,
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  message: { fontSize: 13, lineHeight: 1.45, color: 'text.primary', wordBreak: 'break-word', '& p': { m: 0, mb: 0.5 } },
  footerRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 },
  footerCol: { display: 'flex', flexDirection: 'column', width: '100%' },
  attachments: {
    wrapper: {
      mb: 2,
      borderTop: '1px solid gray',
      borderColor: 'border.main',
      mt: 1,
      pt: 1,
      gap: 0.25,
      flexDirection: 'column',
      display: 'flex',
      flexWrap: 'wrap',
      width: '100%',
    },
    item: { width: '100%' },
  },
  author: {
    mt: 0.5,
    fontWeight: 500,
    color: 'text.secondary',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
    alignItems: 'center',
  },
  authorAvatar: { bgcolor: 'grey.300', width: 20, height: 20, mr: 0.5 },
} as const;
